import React, { useEffect, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { verifyAuth } from './redux/slices/authSlice';
import { LanguageProvider } from './context/LanguageContext';
import { ConsultationModalProvider } from './context/ConsultationModalContext';
import ConsultationModal from './components/ConsultationModal/ConsultationModal';
import './i18n';

// Components
import HomeHeader from './components/HomeHeader/HomeHeader';
import HomeFooter from './components/HomeFooter/HomeFooter';
import NotificationContainer from './components/Notification/NotificationContainer';
// Components
import ProtectedRoute from './components/ProtectedRoute';
import InactivityLogout from './components/InactivityLogout';

// Home page – import direct ca să evităm "Failed to fetch dynamically imported module"
import HomePage from './pages/HomePage/HomePage';

// Lazy load pages
const ContactPage = React.lazy(() => import('./pages/ContactPage/ContactPage'));
const QuizRedirect = React.lazy(() => import('./pages/QuizPage/QuizRedirect'));
const VisaPathwaysPage = React.lazy(() => import('./pages/VisaPathwaysPage/VisaPathwaysPage'));
const HowItWorksPage = React.lazy(() => import('./pages/HowItWorksPage/HowItWorksPage'));
const FeesScopePage = React.lazy(() => import('./pages/FeesScopePage/FeesScopePage'));
const InsightsPage = React.lazy(() => import('./pages/InsightsPage/InsightsPage'));
const ResourceDetailPage = React.lazy(() => import('./pages/ResourceDetailPage/ResourceDetailPage'));
const AboutUsPage = React.lazy(() => import('./pages/AboutUsPage/AboutUsPage'));
const CountryPage = React.lazy(() => import('./pages/CountryPage/CountryPage'));
const LoginPage = React.lazy(() => import('./pages/Auth/LoginPage'));
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const VerifyEmailPage = React.lazy(() => import('./pages/Auth/VerifyEmailPage'));
const VerifyTokenPage = React.lazy(() => import('./pages/Auth/VerifyTokenPage'));
const GoogleCallback = React.lazy(() => import('./pages/Auth/GoogleCallback'));
const ForgotPasswordPage = React.lazy(() => import('./pages/Auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/Auth/ResetPasswordPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage/TermsPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage/PrivacyPolicyPage'));
const NewsletterConfirmed = React.lazy(() => import('./pages/Newsletter/NewsletterConfirmed'));
const NewsletterError = React.lazy(() => import('./pages/Newsletter/NewsletterError'));
const NewsletterConfirm = React.lazy(() => import('./pages/Newsletter/NewsletterConfirm'));
const UnsubscribePage = React.lazy(() => import('./pages/Newsletter/UnsubscribePage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage/NotFoundPage'));

// Visa pages
const VisaTypesPage = React.lazy(() => import('./pages/VisaTypesPage/VisaTypesPage'));
const VisaTypeDetailPage = React.lazy(() => import('./pages/VisaTypeDetailPage/VisaTypeDetailPage'));
const VisaApplicationPage = React.lazy(() => import('./pages/VisaApplicationPage/VisaApplicationPage'));
const CaseWizardPage = React.lazy(() => import('./pages/CaseWizardPage/CaseWizardPage'));
const VisaApplicationsPage = React.lazy(() => import('./pages/VisaApplicationsPage/VisaApplicationsPage'));
const VisaApplicationDetailPage = React.lazy(() => import('./pages/VisaApplicationDetailPage/VisaApplicationDetailPage'));
const ServiceDetailPage = React.lazy(() => import('./pages/ServiceDetailPage/ServiceDetailPage'));
const VisaCategoryPage = React.lazy(() => import('./pages/VisaCategoryPage/VisaCategoryPage'));
const ItalyVisaDetailPage = React.lazy(() => import('./pages/ItalyVisaDetailPage/ItalyVisaDetailPage'));
const PortugalVisaDetailPage = React.lazy(() => import('./pages/PortugalVisaDetailPage/PortugalVisaDetailPage'));
const GreeceVisaDetailPage = React.lazy(() => import('./pages/GreeceVisaDetailPage/GreeceVisaDetailPage'));
const SpainVisaDetailPage = React.lazy(() => import('./pages/SpainVisaDetailPage/SpainVisaDetailPage'));
const FranceVisaDetailPage = React.lazy(() => import('./pages/FranceVisaDetailPage/FranceVisaDetailPage'));
const MaltaVisaDetailPage = React.lazy(() => import('./pages/MaltaVisaDetailPage/MaltaVisaDetailPage'));
const EUCitizenResidencePage = React.lazy(() => import('./pages/EUCitizenResidencePage/EUCitizenResidencePage'));

// Profile routes
import ProfileRoutes from './routes/profile.routes';

// Legacy language-prefixed URLs (e.g. /en/about-us, /ro) redirect to the plain English path.
// The site is English-only since the i18n removal.
const LangRedirect = () => {
  const location = useLocation();
  const match = location.pathname.match(/^\/(en|ro|ru|de|fr|it)(-[a-zA-Z]{2,4})?(\/.*)?$/i);
  const target = (match?.[3] || '/') + location.search + location.hash;
  return <Navigate to={target} replace />;
};

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { needsVerification, loading } = useSelector((state) => state.auth);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Verify authentication on app start if needed
  useEffect(() => {
    if (needsVerification) {
      dispatch(verifyAuth());
    }
  }, [dispatch, needsVerification]);

  // Show loading overlay only for protected routes while verifying auth
  const isProtectedPath = /^\/(dashboard|profile|visa\/apply|visa\/applications|visa\/application)/.test(location.pathname);
  const showAuthBlocking = needsVerification && loading && isProtectedPath;

  if (showAuthBlocking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-DELVANTE-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-DELVANTE-accent mx-auto mb-4"></div>
          <p className="text-DELVATNE-text font-medium">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  const path = location.pathname.replace(/\/$/, '') || '/';
  const isHome = path === '' || path === '/';

  return (
    <HelmetProvider>
      <LanguageProvider>
        <ConsultationModalProvider>
        <div className="app bg-DELVANTE-bg min-h-screen">
          <InactivityLogout />
          <NotificationContainer />
          <ConsultationModal />
          <HomeHeader />

          <main className="main-content">
            <Suspense fallback={
              <div className="min-h-screen flex items-center justify-center bg-DELVANTE-bg">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-DELVANTE-separator border-t-DELVANTE-accent" />
              </div>
            }>
              <Routes>
                {/* Home Page - Gateway */}
                <Route path="/" element={<HomePage />} />

                {/* Profile routes */}
                <Route path="/profile/*" element={<ProfileRoutes />} />

                {/* Contact */}
                <Route path="/contact" element={<ContactPage />} />

                {/* Info pages - from navbar */}
                <Route path="/visa-pathways" element={<VisaPathwaysPage />} />
                <Route path="/visa-pathways/:category" element={<VisaCategoryPage />} />
                <Route path="/how-it-works" element={<HowItWorksPage />} />
                <Route path="/fees-scope" element={<FeesScopePage />} />
                <Route path="/insights" element={<InsightsPage />} />
                <Route path="/insights/:slug" element={<ResourceDetailPage />} />
                <Route path="/about-us" element={<AboutUsPage />} />
                <Route path="/country/:country" element={<CountryPage />} />

                {/* Italy Visa Detail Pages */}
                <Route path="/country/italy/visa/:visaKey" element={<ItalyVisaDetailPage />} />

                {/* Portugal Visa Detail Pages */}
                <Route path="/country/portugal/visa/:visaKey" element={<PortugalVisaDetailPage />} />

                {/* Greece Visa Detail Pages */}
                <Route path="/country/greece/visa/:visaKey" element={<GreeceVisaDetailPage />} />

                {/* Spain Visa Detail Pages */}
                <Route path="/country/spain/visa/:visaKey" element={<SpainVisaDetailPage />} />

                {/* France Visa Detail Pages */}
                <Route path="/country/france/visa/:visaKey" element={<FranceVisaDetailPage />} />

                {/* Malta Visa Detail Pages */}
                <Route path="/country/malta/visa/:visaKey" element={<MaltaVisaDetailPage />} />

                {/* EU Citizen Residence Registration */}
                <Route path="/eu-citizens/residence" element={<EUCitizenResidencePage />} />

                {/* Auth routes */}
                <Route path="/login" element={<LoginPage />} />

                {/* Public self-registration is disabled — accounts are created by the admin after payment */}
                <Route path="/register" element={<Navigate to="/login" replace />} />

                {/* Dashboard - Protected */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

                <Route path="/verify-email" element={<VerifyEmailPage />} />

                <Route path="/verify/:token" element={<VerifyTokenPage />} />

                <Route path="/auth/google/callback" element={<GoogleCallback />} />

                <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Legal pages */}
                <Route path="/terms" element={<TermsPage />} />

                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

                {/* Newsletter pages */}
                <Route path="/newsletter/confirm/:token" element={<NewsletterConfirm />} />

                <Route path="/newsletter/confirmed" element={<NewsletterConfirmed />} />

                <Route path="/newsletter/error" element={<NewsletterError />} />

                {/* Unsubscribe page */}
                <Route path="/unsubscribe" element={<UnsubscribePage />} />

                {/* Visa pages */}
                <Route path="/visa/types" element={<VisaTypesPage />} />
                <Route path="/visa/:slug" element={<VisaTypeDetailPage />} />
                <Route path="/visa/apply/:visaTypeId" element={<ProtectedRoute><CaseWizardPage /></ProtectedRoute>} />
                <Route path="/visa/applications" element={<ProtectedRoute><VisaApplicationsPage /></ProtectedRoute>} />
                <Route path="/visa/application/:id" element={<ProtectedRoute><VisaApplicationDetailPage /></ProtectedRoute>} />

                {/* Service Detail Page */}
                <Route path="/services/:slug" element={<ServiceDetailPage />} />

                {/* Quiz - redirect to the quiz subdomain */}
                <Route path="/quiz" element={<QuizRedirect />} />

                {/* Legacy language prefixes (en/ro/ru/de/fr/it) — redirect to plain URLs */}
                <Route path="/:lang" element={<LangRedirect />} />
                <Route path="/:lang/*" element={<LangRedirect />} />

                {/* 404 page */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </main>

          <HomeFooter />
        </div>
        </ConsultationModalProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
